import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Flex, Button, ButtonGroup, Text, SimpleGrid, Spinner, Center, VStack, HStack,
  IconButton, Menu, MenuButton, MenuList, MenuItem, useToast, useDisclosure, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Input, Textarea,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay,
} from '@chakra-ui/react';
import { FiPlus, FiMoreVertical, FiTrash2, FiEdit2, FiUsers, FiX, FiUser, FiUserPlus } from 'react-icons/fi';
import { LuGitBranch, LuNetwork } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import PageHeading from '../../components/shared/PageHeading';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { metricTreeApi } from './metricTreeApi';
import { TEMPLATES, templateByKey } from './metricTreeTemplates';
import usePagePermissions from '../../hooks/usePagePermissions';

export default function MetricTreeList() {
  const navigate = useNavigate();
  const toast = useToast();

  const { loading: featurePermissionsLoading, can } = usePagePermissions('analytics/metric-tree');

  const metricTreeListPermission = can("metric-tree-list");
  // No separate create/edit slug exists for Metric Tree (see PERMISSION_SLUGS.md) —
  // opening a tree (to view OR edit, the canvas handles both) and creating a new one
  // both reuse metric-tree-view, the same slug MetricTreeCanvas.jsx gates on.
  const metricTreeViewPermission = can("metric-tree-view");
  const metricTreeDeletePermission = can("metric-tree-delete");
  const metricTreeCreatePermission = can("metric-tree-create");
  const metricTreeSharePermission = can("metric-tree-share");

  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  // MT-01: scope filter tabs (All / Personal / Team / Shared with me).
  const [scopeTab, setScopeTab] = useState('all');

  const currentUserId = useMemo(() => {
    try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return String(u?.id || u?.username || ''); }
    catch (_) { return ''; }
  }, []);
  const isOwnerOf = (t) => currentUserId !== '' && t?.owner_id != null && String(t.owner_id) === currentUserId;
  const [creatingKey, setCreatingKey] = useState(null); // which template card is creating
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  // MT-19: distinguish a genuine empty collection from a load FAILURE, so a 401/403
  // never renders the first-run empty state (which implies the data is gone).
  const [loadError, setLoadError] = useState(null);

  // BUG-MT-14: visibility chosen at creation — Personal / Team / Shared. 'shared'
  // reveals the same person picker the Share dialog uses and sends the selected
  // members with the create payload (scope stays personal; editors carry access).
  const [newVisibility, setNewVisibility] = useState('personal');
  const [shareSelected, setShareSelected] = useState([]); // [{ id, name, email }]
  const [userDir, setUserDir] = useState([]);
  const [userDirState, setUserDirState] = useState('idle'); // 'idle'|'loading'|'ready'|'error'
  const [userQuery, setUserQuery] = useState('');

  // Reset all creation fields whenever the modal opens, and prefetch the directory
  // so the Shared picker is ready.
  const openCreate = () => {
    setNewName(''); setNewDescription(''); setNewVisibility('personal');
    setShareSelected([]); setUserQuery('');
    onOpen();
    setUserDirState('loading');
    metricTreeApi.listUsers()
      .then((r) => { setUserDir(r.data || []); setUserDirState(r.error ? 'error' : 'ready'); })
      .catch(() => setUserDirState('error'));
  };
  const addShareUser = (u) => { setShareSelected((p) => (p.some((s) => s.id === u.id) ? p : [...p, u])); setUserQuery(''); };
  const removeShareUser = (uid) => setShareSelected((p) => p.filter((s) => s.id !== uid));
  const shareCandidates = userDir
    .filter((u) => !shareSelected.some((s) => s.id === u.id))
    .filter((u) => {
      const q = userQuery.trim().toLowerCase();
      if (!q) return true;
      return u.name.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || u.id.includes(q);
    })
    .slice(0, 25);

  // Delete confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const {
    isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = useRef();

  const loadTrees = () => {
    setLoading(true);
    setLoadError(null);
    metricTreeApi
      .list()
      .then((r) => setTrees(Array.isArray(r?.data) ? r.data : []))
      .catch((e) => {
        const status = e?.response?.status;
        // BUG-MT-19: an expired session must redirect to login, NOT fall through
        // to the first-run empty state (which reads as "all your trees are gone").
        // Reserve the empty state for a genuine 200 with no rows. A 403 means the
        // session is valid but lacks permission — that's a "No Access" message,
        // not a logout (PERM-119: this used to log the user out on 403 too).
        if (status === 401) {
          try { localStorage.removeItem('customer-portal'); } catch (_) { /* ignore */ }
          navigate('/'); // the login page is mounted at "/" (App.js), not "/login"
          return;
        }
        if (status === 403) {
          setLoadError("You don't have permission to view this sections.");
          return;
        }
        setLoadError(e?.response?.data?.message || e.message || 'Failed to load metric trees');
        toast({
          title: 'Failed to load metric trees',
          description: e?.response?.data?.message || e.message,
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (featurePermissionsLoading || !can('metric-tree-list')) return;
    loadTrees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featurePermissionsLoading]);

  // MT-01: client-side scope filter over the single list fetch (which already
  // returns my personal trees + team trees + personal trees shared with me).
  const visibleTrees = useMemo(() => {
    if (scopeTab === 'personal') return trees.filter((t) => t.scope === 'personal' && isOwnerOf(t));
    if (scopeTab === 'team') return trees.filter((t) => t.scope === 'team');
    if (scopeTab === 'shared') return trees.filter((t) => t.scope === 'personal' && !isOwnerOf(t));
    return trees;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trees, scopeTab, currentUserId]);
  const scopeTabs = [
    { key: 'all', label: 'All' },
    { key: 'personal', label: 'Personal' },
    { key: 'team', label: 'Team' },
    { key: 'shared', label: 'Shared with me' },
  ];

  // Create a tree from a starter template (or blank) and open it. Templates
  // scaffold real placeholder nodes/edges (Mixpanel parity) instead of a blank canvas.
  const handlePick = async (key) => {
    if (creatingKey) return;
    setCreatingKey(key);
    try {
      const typedName = newName.trim();
      const description = newDescription.trim() || undefined;
      // BUG-MT-14: carry the chosen visibility into the create payload. Team →
      // scope:'team'. Shared → scope:'personal' + the selected members as editors.
      // Personal → scope:'personal', no editors.
      const visibility = {
        scope: newVisibility === 'team' ? 'team' : 'personal',
        editor_user_ids: newVisibility === 'shared' ? shareSelected.map((s) => s.id) : [],
      };
      let payload;
      if (key === 'blank') {
        // Default to all_time so compute returns non-zero values against historical data.
        payload = { name: typedName || 'Untitled metric tree', description, default_time_frame: 'all_time', nodes: [], edges: [], layout: {}, ...visibility };
      } else {
        const tpl = templateByKey(key);
        const built = tpl.build();
        payload = {
          name: typedName || built.name, // typed name wins; else the template's default
          description,
          default_time_frame: built.default_time_frame || 'all_time',
          nodes: built.nodes,
          edges: built.edges,
          layout: { orientation: tpl.orientation },
          ...visibility,
        };
      }
      const res = await metricTreeApi.create(payload);
      const id = res?.data?._id;
      onClose();
      // BUG-MT-18: a freshly created tree opens directly in EDIT mode (nothing to
      // preview) — pass the flag the canvas reads from navigation state.
      // PERM-190: justCreated additionally lets PermissionRoute's allowIfState grant
      // entry to THIS one tree for a create-only user, since the route no longer
      // accepts metric-tree-create on its own.
      if (id) navigate(`/analytics/metric-tree/${id}`, { state: { edit: true, justCreated: true } });
      else loadTrees();
    } catch (e) {
      toast({
        title: 'Create failed',
        description: e?.response?.data?.message || e.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCreatingKey(null);
    }
  };

  const askDelete = (tree) => {
    setDeleteTarget(tree);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await metricTreeApi.remove(deleteTarget._id);
      setTrees((prev) => prev.filter((t) => t._id !== deleteTarget._id));
      toast({ title: 'Metric tree deleted', status: 'success', duration: 2500, isClosable: true });
      onDeleteClose();
      setDeleteTarget(null);
    } catch (e) {
      toast({
        title: 'Delete failed',
        description: e?.response?.data?.message || e.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (featurePermissionsLoading) {
    return (
      <Box>
        <Header header="Metric Tree" />
        <Center h="400px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Center>
      </Box>
    );
  }

  return (
    <Box>
      <Header header="Metric Tree" />

      {metricTreeListPermission ? (
      <>
      <BoxWrapper>
        <PageHeading icon={LuNetwork} title="Metric Trees" subtitle="Build a visual tree of metrics and discover how they correlate." mb={6}>
          {metricTreeCreatePermission && (
            <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>
              New metric tree
            </Button>
          )}
        </PageHeading>

        {/* MT-01: scope filter tabs */}
        {!loading && trees.length > 0 && (
          <ButtonGroup size="sm" isAttached variant="outline" mb={5}>
            {scopeTabs.map((t) => (
              <Button
                key={t.key}
                onClick={() => setScopeTab(t.key)}
                colorScheme={scopeTab === t.key ? 'blue' : 'gray'}
                variant={scopeTab === t.key ? 'solid' : 'outline'}
              >
                {t.label}
              </Button>
            ))}
          </ButtonGroup>
        )}

        {loading ? (
          <Center py={20}><Spinner size="lg" color="blue.500" /></Center>
        ) : loadError ? (
          // BUG-MT-19: a real load failure shows an error + retry, never the
          // first-run empty state (401/403 already redirected to login above).
          <Center py={20}>
            <VStack spacing={3}>
              <Text fontWeight="700" color="gray.600">Couldn&apos;t load your metric trees</Text>
              <Text fontSize="sm" color="gray.400">{loadError}</Text>
              <Button mt={2} colorScheme="blue" onClick={loadTrees}>Retry</Button>
            </VStack>
          </Center>
        ) : trees.length === 0 ? (
          <Center py={20}>
            <VStack spacing={3}>
              <LuGitBranch size={48} color="#CBD5E0" />
              <Text fontWeight="700" color="gray.600">No metric trees yet</Text>
              <Text fontSize="sm" color="gray.400">Create your first tree to get started.</Text>
              {metricTreeCreatePermission && (
                <Button mt={2} leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate}>
                  New metric tree
                </Button>
              )}
            </VStack>
          </Center>
        ) : visibleTrees.length === 0 ? (
          <Center py={16}>
            <VStack spacing={2}>
              <Text fontWeight="700" color="gray.600">No trees in this view</Text>
              <Text fontSize="sm" color="gray.400">
                {scopeTab === 'shared' ? 'No trees have been shared with you yet.' : 'Nothing here — try a different tab.'}
              </Text>
            </VStack>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {visibleTrees.map((tree) => (
              <Box
                key={tree._id}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p={4}
                bg="white"
                _hover={{ borderColor: 'blue.400', boxShadow: 'md' }}
                transition="all 0.15s"
                cursor={metricTreeViewPermission ? 'pointer' : 'default'}
                onClick={() => { if (metricTreeViewPermission) navigate(`/analytics/metric-tree/${tree._id}`); }}
              >
                <Flex justify="space-between" align="flex-start">
                  <HStack spacing={2} flex={1} minW={0}>
                    <Box fontSize="xl">{tree.emoji || '🌳'}</Box>
                    <Box minW={0}>
                      <Text fontWeight="700" noOfLines={1}>{tree.name}</Text>
                      {tree.description && (
                        <Text fontSize="xs" color="gray.500" noOfLines={2}>{tree.description}</Text>
                      )}
                    </Box>
                  </HStack>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<FiMoreVertical />}
                      variant="ghost"
                      size="sm"
                      aria-label="Actions"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <MenuList onClick={(e) => e.stopPropagation()}>
                      {metricTreeViewPermission && (
                        <MenuItem icon={<FiEdit2 />} onClick={() => navigate(`/analytics/metric-tree/${tree._id}`)}>
                          Open
                        </MenuItem>
                      )}
                      {/* MT-01: only the owner may delete (backend enforces the same). */}
                      {isOwnerOf(tree) && metricTreeDeletePermission && (
                        <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => askDelete(tree)}>
                          Delete
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </Flex>
                {/* MT-01: scope + sharing badges */}
                <HStack mt={3} spacing={2}>
                  <Badge
                    colorScheme={tree.scope === 'team' ? 'purple' : 'gray'}
                    variant="subtle" textTransform="capitalize" borderRadius="full" px={2}
                  >
                    {tree.scope || 'personal'}
                  </Badge>
                  {tree.scope === 'personal' && !isOwnerOf(tree) && (
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
                      Shared with you
                    </Badge>
                  )}
                  {isOwnerOf(tree) && Array.isArray(tree.editor_user_ids) && tree.editor_user_ids.length > 0 && (
                    <HStack spacing={1} bg="#e0e7ff" color="#3730a3" borderRadius="full" px={2} py={0.5}>
                      <FiUsers size={11} />
                      <Text fontSize="xs" fontWeight={700}>Shared · {tree.editor_user_ids.length}</Text>
                    </HStack>
                  )}
                </HStack>
                <HStack mt={3} spacing={4} fontSize="xs" color="gray.400">
                  <Text>{(tree.default_time_frame || 'all_time').replace(/_/g, ' ')}</Text>
                  {tree.updated_at && (
                    <Text>Updated {new Date(tree.updated_at).toLocaleDateString()}</Text>
                  )}
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </BoxWrapper>

      {/* Create modal — pick a starter template (Mixpanel-style) */}
      <Modal isOpen={isOpen} onClose={creatingKey ? undefined : onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">New metric tree</ModalHeader>
          {!creatingKey && <ModalCloseButton />}
          <ModalBody pb={2}>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Tree name</FormLabel>
              <Input
                autoFocus
                size="sm"
                placeholder="e.g. Revenue drivers (optional — we'll name it from the template)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                isDisabled={!!creatingKey}
              />
            </FormControl>
            <FormControl mb={5}>
              <FormLabel fontSize="sm">Description <Text as="span" color="gray.400" fontWeight="400">(optional)</Text></FormLabel>
              <Textarea
                size="sm"
                placeholder="What is this tree for?"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={2}
                isDisabled={!!creatingKey}
              />
            </FormControl>
            {/* BUG-MT-14: visibility chosen at creation. */}
            <FormControl mb={newVisibility === 'shared' ? 3 : 5}>
              <FormLabel fontSize="sm">Visibility</FormLabel>
              <ButtonGroup size="sm" isAttached variant="outline" isDisabled={!!creatingKey}>
                {[
                  { key: 'personal', label: 'Personal', icon: <FiUser /> },
                  ...(metricTreeSharePermission ? [
                    { key: 'team', label: 'Team', icon: <FiUsers /> },
                    { key: 'shared', label: 'Shared', icon: <FiUserPlus /> },
                  ] : []),
                ].map((v) => (
                  <Button
                    key={v.key}
                    leftIcon={v.icon}
                    onClick={() => setNewVisibility(v.key)}
                    colorScheme={newVisibility === v.key ? 'blue' : 'gray'}
                    variant={newVisibility === v.key ? 'solid' : 'outline'}
                  >
                    {v.label}
                  </Button>
                ))}
              </ButtonGroup>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {newVisibility === 'team'
                  ? 'Everyone on your team can view and edit this tree.'
                  : newVisibility === 'shared'
                    ? 'Only the people you add below can view and edit this tree.'
                    : 'Only you can see this tree until you share it.'}
              </Text>
            </FormControl>

            {/* BUG-MT-14: Shared reveals the same person picker as the Share dialog. */}
            {newVisibility === 'shared' && (
              <FormControl mb={5}>
                <FormLabel fontSize="sm">People with access</FormLabel>
                {shareSelected.length > 0 && (
                  <HStack spacing={2} wrap="wrap" mb={2}>
                    {shareSelected.map((s) => (
                      <HStack key={s.id} spacing={1} bg="#e0e7ff" color="#3730a3" borderRadius="full" pl={2} pr={1} py={1}>
                        <Text fontSize="xs" fontWeight={600}>{s.name}</Text>
                        <IconButton aria-label={`Remove ${s.name}`} icon={<FiX />} size="2xs" variant="ghost"
                          color="#3730a3" minW={4} h={4} onClick={() => removeShareUser(s.id)} isDisabled={!!creatingKey} />
                      </HStack>
                    ))}
                  </HStack>
                )}
                <Input
                  size="sm"
                  placeholder="Search people by name or email…"
                  value={userQuery}
                  onChange={(e) => setUserQuery(e.target.value)}
                  isDisabled={!!creatingKey}
                />
                {userQuery.trim() && (
                  <Box mt={1} maxH="180px" overflowY="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
                    {userDirState === 'loading' && (
                      <HStack p={3} color="gray.500"><Spinner size="xs" /><Text fontSize="sm">Loading people…</Text></HStack>
                    )}
                    {userDirState === 'error' && (
                      <Text p={3} fontSize="sm" color="orange.500">Couldn&apos;t load the user directory (need the user-list permission).</Text>
                    )}
                    {userDirState === 'ready' && shareCandidates.length === 0 && (
                      <Text p={3} fontSize="sm" color="gray.500">No users found matching “{userQuery.trim()}”.</Text>
                    )}
                    {userDirState === 'ready' && shareCandidates.map((u) => (
                      <HStack key={u.id} as="button" type="button" w="100%" justify="space-between"
                        px={3} py={2} _hover={{ bg: 'gray.50' }} onClick={() => addShareUser(u)} textAlign="left">
                        <Box textAlign="left">
                          <Text fontSize="sm" fontWeight={600}>{u.name}</Text>
                          {u.email ? <Text fontSize="xs" color="gray.500">{u.email}</Text> : null}
                        </Box>
                        <FiPlus />
                      </HStack>
                    ))}
                  </Box>
                )}
              </FormControl>
            )}

            <Text fontSize="sm" color="gray.500" mb={3} fontWeight="600">
              Choose a starting point
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {TEMPLATES.map((t) => {
                const busy = creatingKey === t.key;
                const dimmed = creatingKey && !busy;
                return (
                  <Box
                    key={t.key}
                    role="button"
                    tabIndex={0}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="lg"
                    p={4}
                    opacity={dimmed ? 0.5 : 1}
                    cursor={creatingKey ? 'default' : 'pointer'}
                    _hover={creatingKey ? undefined : { borderColor: 'blue.400', boxShadow: 'md' }}
                    transition="all 0.15s"
                    onClick={() => !creatingKey && handlePick(t.key)}
                    onKeyDown={(e) => { if (!creatingKey && (e.key === 'Enter' || e.key === ' ')) handlePick(t.key); }}
                  >
                    <Flex align="center" gap={3} mb={2}>
                      <Box w="36px" h="36px" borderRadius="md" bg={t.accent} display="grid" placeItems="center" flexShrink={0}>
                        <LuGitBranch
                          size={18}
                          color="#0F1A2B"
                          style={{ transform: t.orientation === 'horizontal' ? 'rotate(-90deg)' : 'none' }}
                        />
                      </Box>
                      <Text fontWeight="700" flex={1}>{t.title}</Text>
                      {busy && <Spinner size="sm" color="blue.500" />}
                    </Flex>
                    <Text fontSize="sm" color="gray.500">{t.blurb}</Text>
                    <Text fontSize="xs" color="gray.400" mt={2}>
                      {t.orientation === 'vertical' ? 'Top-down layout' : 'Left-to-right layout'}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button variant="link" size="sm" colorScheme="blue" isDisabled={!!creatingKey} onClick={() => handlePick('blank')}>
              Start with a blank canvas
            </Button>
            <Button variant="ghost" onClick={onClose} isDisabled={!!creatingKey}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="700">Delete metric tree</AlertDialogHeader>
            <AlertDialogBody>
              Delete <b>{deleteTarget?.name}</b>? This can't be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>Cancel</Button>
              <Button colorScheme="red" ml={3} onClick={handleDelete} isLoading={deleting} loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      </>
      ) : (
        <Center h="400px">
          <Box textAlign="center">
            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
              No Access
            </Text>
            <Text fontSize="sm" color="gray.500">
              You don't have permission to view this sections.
            </Text>
          </Box>
        </Center>
      )}
    </Box>
  );
}
